<script lang="ts">
export default {
  name: 'FiduciaryBenchmarking',
};
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch, reactive } from 'vue';
import AccountBenchmarkViewOptions from '@/components/AccountBenchmarkViewOptions.vue';
import ClientFilter from '@/components/ClientFilter.vue';
import { benchmarkService, referenceService } from '@/services/api';
import { Account } from '@/types/Account';
import { AccountBenchmark, Benchmark, FiduciaryBenchmark } from '@/types/Benchmark';
import { AttributeOption, both, calc, Chart, Column, PUBLIC, sameOwnershipType, SHOW_ALL } from './Benchmarking';
import BenchmarkingCharts from './BenchmarkingCharts.vue';
import BenchmarkingTable from './BenchmarkingTable.vue';
import { calcAverage, calcMedian } from '@/utilities/Number';
import GenericFilter from '@/components/GenericFilter.vue';
import { compactMoney } from '@/utilities/Money';
import FilterSlider from '@/components/FilterSlider.vue';
import { useRoute } from 'vue-router';
import OwnershipTypeFilter from '@/components/OwnershipTypeFilter.vue';
import { LabelFilterType } from '@/types/components/FilterType';

const $route = useRoute();

const props = defineProps<{
  account: Account;
}>();

const emit = defineEmits<{
  'needs-update': [];
}>();

// -----
// data
// -----
const isLoading = ref<boolean>(false);
const accountData = ref<FiduciaryBenchmark[]>([]);
const data = ref<FiduciaryBenchmark[]>([]);
const selectedClients = ref<string[]>([]);
const appliedClients = ref<string[]>([]);
const appliedLabelDisplay = ref<string>(SHOW_ALL);
const appliedPlanAssetsRange = ref<[number, number]>();
const attributeTypes = ref<AttributeOption[]>([]);
const selectedAttributeTypes = ref<number[]>([]);
const industries = ref<LabelFilterType<number>[]>([]);
const selectedIndustries = ref<number[]>([]);
const accountInfo = ref<AccountBenchmark>({} as AccountBenchmark);
const benchmarkTableKey = ref(0);

const ownershipTypeFilter = reactive({
  showPrivate: false,
  showPublic: false,
});

// ---------
// computed
// ---------
const clients = computed(() => {
  const clients = data.value.reduce((acc, d) => {
    const isCurrentClient = d.accountId === $route.params.id;
    if (isCurrentClient) {
      return acc;
    }
    const alreadyInAccumulator = acc.some(i => d.accountName && i.text.trim().toLowerCase() === d.accountName.trim().toLowerCase());
    if (alreadyInAccumulator) {
      return acc;
    }
    if (props.account.ownershipType !== d.ownershipType) {
      return acc;
    }
    acc.push({ text: d.accountName || '', value: d.accountName || '' });
    return acc;
  }, [] as { text: string, value: string }[]);
  clients.sort((a, b) => a.text.localeCompare(b.text));
  return clients;
});

const otherData = computed<FiduciaryBenchmark[]>(() => {
  if (appliedClients.value.length > 0) {
    return data.value
      .filter(d => appliedClients.value.includes(d.accountName || ''))
      .filter(filterByOwnershipType)
      .filter(filterByIndustry)
      .filter(d => ('' + d.ownershipType).trim().toLowerCase() === ('' + props.account.ownershipType).trim().toLowerCase())
      .filter(filterByPlanAssetsRange)
      .map(transformAccountName) as FiduciaryBenchmark[];
  }

  return data.value
    .filter(d => d.accountId !== $route.params.id)
    .filter(filterByOwnershipType)
    .filter(filterByIndustry)
    .filter(filterByPlanAssetsRange)
    .map(transformAccountName) as FiduciaryBenchmark[];
});

const averageData = computed<FiduciaryBenchmark[]>(() => {
  return [{
    key: 'avg',
    accountName: ' ',
    policyYear: ' ' as never,
    programType: ' ',
    sic: '',
    industryName: 'Average',
    stockTicker: '',
    ownershipType: '',
    primaryCarrierGroup: '',
    primaryCarrierGroupID: '',
    policyEffectiveDate: '',
    planFiduciaryAssets: calc(otherData.value, 'planFiduciaryAssets', calcAverage),
    totalRetention: calc(otherData.value, 'totalRetention', calcAverage, true),
    totalLimit: calc(otherData.value, 'totalLimit', calcAverage),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcAverage),
    maxPrimaryPPM: calc(otherData.value, 'maxPrimaryPPM', calcAverage),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcAverage),
    frozen: true,
  }];
});

const medianData = computed<FiduciaryBenchmark[]>(() => {
  return [{
    key: 'med',
    accountName: ' ',
    policyYear: ' ' as never,
    programType: ' ',
    sic: '',
    industryName: 'Median',
    stockTicker: '',
    ownershipType: '',
    primaryCarrierGroup: '',
    primaryCarrierGroupID: '',
    policyEffectiveDate: '',
    planFiduciaryAssets: calc(otherData.value, 'planFiduciaryAssets', calcMedian),
    totalRetention: calc(otherData.value, 'totalRetention', calcMedian, true),
    totalLimit: calc(otherData.value, 'totalLimit', calcMedian),
    totalPrimaryLimit: calc(otherData.value, 'totalPrimaryLimit', calcMedian),
    maxPrimaryPPM: calc(otherData.value, 'maxPrimaryPPM', calcMedian),
    firstLayerILF: calc(otherData.value, 'firstLayerILF', calcMedian),
    frozen: true,
  }];
});

const isPublic = computed(() => props.account.ownershipType?.trim().toLowerCase() === PUBLIC);
const isPrivate = computed(() => !isPublic.value);

const columns = computed(() => {
  const columns: (Column<FiduciaryBenchmark> | undefined)[] = [
    { fieldName: 'policyYear', type: 'number', title: 'Year' },
    { fieldName: 'accountName', type: 'nosort', classes: 'truncated', title: 'Client', url: (data) => `/account/${data.accountId}/overview`, showInfoIcon: 'isPackaged', showInfoFieldName: 'packagedCoverageTypes' },
    { fieldName: 'industryName', type: '', title: 'Industry', url: (data) => `/account/${data.accountId}/edit`, accessoryIcon: 'pencil' },
    { fieldName: 'ownershipType', type: '', title: 'Ownership' },
    { fieldName: 'planFiduciaryAssets', type: 'compactmoney', title: 'Plan Assets', classes: isPrivate.value ? 'sorted' : '', filter: 'compactMoney', exposureCategory: 'Plan Fiduciary Assets' },
    { fieldName: 'totalLimit', type: 'compactmoney', title: 'Total Limits', filter: 'compactMoney', url: (data) => `/account/${data.accountId}/policy-detail`, accessoryIcon: 'pencil' },
    { fieldName: 'totalRetention', type: 'compactmoney', title: 'Retention', filter: 'compactMoney' },
    { fieldName: 'totalPrimaryLimit', type: 'compactmoney', title: 'Primary Limit', filter: 'compactMoney' },
    { fieldName: 'maxPrimaryPPM', type: 'compactmoney', title: 'Primary PPM', filter: 'compactMoney' },
    { fieldName: 'firstLayerILF', type: 'text-right', title: '1st XS Rate', filter: 'percentage' },
    { fieldName: 'primaryCarrierGroup', type: '', title: 'Primary Carrier', url: (data) => `/carrier/${data.primaryCarrierGroupID}/overview` },
    { fieldName: 'policyEffectiveDate', type: 'date', title: 'Effective Date', filter: 'date', useFilteredExport: true }
  ];
  return columns.filter(x => x) as Column<FiduciaryBenchmark>[];
});

const charts = computed(() => {
  const charts: (Chart<FiduciaryBenchmark> | undefined)[] = [
    { fieldName: 'planFiduciaryAssets', title: 'Plan Fiduciary Assets' },
    { fieldName: 'totalLimit', title: 'Total Limits' },
    { fieldName: 'totalRetention', title: 'Retention' },
    { fieldName: 'totalPrimaryLimit', title: 'Primary Limit' },
    { fieldName: 'maxPrimaryPPM', title: 'Primary PPM' },
    { fieldName: 'firstLayerILF', title: '1st XS Rate', isPercentage: true }
  ];
  return charts.filter(x => x) as Chart<FiduciaryBenchmark>[];
});

const maxPlanAssets = computed(() => {
  const vals = data.value.map(d => d.planFiduciaryAssets).filter(d => !!d) as number[];
  return vals.length === 0 ? 0 : Math.max(...vals);
});

const selectedIndustriesSet = computed(() => {
  return selectedIndustries.value.reduce((acc, i) => {
    acc.add(i)
    return acc;
  }, new Set<number>());
});

watch(selectedAttributeTypes, async () => {
  await refresh();
});

watch(otherData, () => {
  // the apptable sorting interferes with reactive table data... this forces a re-render
  benchmarkTableKey.value += 1;
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  await refresh();
});

// ----------
// functions
// ----------
function transformAccountName(d: FiduciaryBenchmark): FiduciaryBenchmark {
  return ({ ...d, accountName: appliedLabelDisplay.value === SHOW_ALL ? d.accountName : d.accountNameAlt });
}

function applyClientFilter(): void {
  appliedClients.value = selectedClients.value;
}

function applyViewOptions(selectedLabels: string): void {
  appliedLabelDisplay.value = selectedLabels;
}

async function refresh() {
  isLoading.value = true;
  const [dataResult, attributeTypesResult, account] = await Promise.all([
    benchmarkService.fiduciary(selectedAttributeTypes.value),
    referenceService.accountAttributeTypes(),
    benchmarkService.account($route.params.id as string),
  ]);

  accountInfo.value = account;
  attributeTypesResult.sort((a, b) => a.name.localeCompare(b.name));
  attributeTypes.value = attributeTypesResult
    .filter(t => sameOwnershipType(t, props.account.ownershipType) || both(t))
    .map(t => ({ label: t.name, value: t.id }));
  data.value = (dataResult || [])
    .map((d, i) => ({
      ...d,
      packagedCoverageTypes: `Packaged with ${d.packagedCoverageTypes}`,
      accountNameAlt: `Company ${i + 1}`
    }));

  // populate industries filter

  if (industries.value.length === 0) {
    industries.value = Array.from(data.value.reduce((acc, d) => {
      if (d.industryId && d.industryName) {
        acc.set(d.industryId, { label: d.industryName, value: d.industryId });
      }
      return acc;
    }, new Map<number, LabelFilterType<number>>())
      .values())
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // upon first load, save off the accountData to populate the first row of the benchmarking table

  if (accountData.value.length === 0) {
    const match = data.value.filter(d => d.accountId === $route.params.id);

    if (match.length > 0) {
      const account = match.reduce((obj, d) => {
        if (!obj.policyYear) return d;
        if (!d.policyYear) return obj;
        if (obj.policyYear < d.policyYear) obj = d;
        return obj;
      }, { ...match[0] });
      accountData.value = [{ ...account, frozen: true }];
    }
    else {
      accountData.value = [{
        key: 'account',
        accountId: account.accountId,
        accountName: account.accountName,
        frozen: true,
        sic: account.sic || '',
        stockTicker: account.stockTicker || '',
        ownershipType: account.ownershipType || '',
        planFiduciaryAssets: account.planFiduciaryAssets,
      }]
    }
  }

  isLoading.value = false;
}

function filterByPlanAssetsRange(d: FiduciaryBenchmark): boolean {
  if (appliedPlanAssetsRange.value) {
    const min = appliedPlanAssetsRange.value[0];
    const max = appliedPlanAssetsRange.value[1];
    if (!d.planFiduciaryAssets) return false;
    return d.planFiduciaryAssets >= min && d.planFiduciaryAssets <= max;
  }
  return true;
}

function filterByOwnershipType(d: FiduciaryBenchmark): boolean {
  if (!ownershipTypeFilter.showPublic && !ownershipTypeFilter.showPrivate) return true; // no filter is set

  switch(d.ownershipType) {
    case 'Public':
      return ownershipTypeFilter.showPublic;
    case 'Private':
      return ownershipTypeFilter.showPrivate;
    default:
      return false;
  }
}

function filterByIndustry(d: FiduciaryBenchmark): boolean {
  if (selectedIndustries.value.length === 0) return true;
  if (!d.industryId) return false;
  return selectedIndustriesSet.value.has(d.industryId);
}

function planAssetsChanged(range: number[]) {
  // the FilterSlider :values is represented in millions data model is not -> converting it back
  appliedPlanAssetsRange.value = range.length === 2 ? [range[0] * 1000000, range[1] * 1000000] : undefined;
}
</script>

<template>
  <BenchmarkingTable titleText="Fiduciary Benchmarking" :key="benchmarkTableKey"
                     :accountData="accountData" :otherData="otherData as Benchmark[]"
                     :averageData="averageData as Benchmark[]" :medianData="medianData as Benchmark[]"
                     :columns="columns as Column[]" :loading="isLoading"
                     @needs-update="emit('needs-update')"
  >
    <template v-slot:charts>
      <BenchmarkingCharts :accountData="accountData as Benchmark[]" :otherData="otherData as Benchmark[]" :charts="charts as Chart[]"/>
    </template>

    <template v-slot:filterbar>
      <slot name="filterbar"/>
      <ClientFilter v-model="selectedClients" :items="clients" @change="applyClientFilter"/>

      <FilterSlider title="Plan Assets" description="Filter clients with plan fiduciary assets between these values."
                    :values="[0, Math.ceil(maxPlanAssets / 1000000)]"
                    :value-formatter-fn="(val) => val ? compactMoney(val * 1000000) : ''"
                    min-title="Min (Millions)" max-title="Max (Millions)"
                    @update:model-value="planAssetsChanged" data-testid="plan-assets-filter-activator" dataTestPrefix="plan-assets" />

      <GenericFilter :types="attributeTypes" chip-title-field="label"
                     v-model="selectedAttributeTypes"
                     title="Client Attributes" data-test="client-type-filter" />

      <OwnershipTypeFilter v-model:show-public="ownershipTypeFilter.showPublic"
                           v-model:show-private="ownershipTypeFilter.showPrivate" />

      <GenericFilter
        title="Industry" chip-title-field="label"
        :types="industries"
        v-model="selectedIndustries"
      />

      <AccountBenchmarkViewOptions :labels="appliedLabelDisplay" @change="applyViewOptions"/>

    </template>
  </BenchmarkingTable>
</template>

